<template>
  <div
    class="group flex h-control justify-between py-[8px] pl-[12px] pr-[20px] transition-colors duration-150 hover:bg-[--v3-input-lists-inputlistheaderbg]"
    :class="{
      'bg-[--v3-input-lists-inputlistheaderbg]': isDragOver || selected
    }"
    @dragenter="handleDragEnter"
    @dragleave="handleDragLeave"
    @drop.prevent="handleDrop"
    @dragover.prevent=""
  >
    <div
      class="pointer-events-none flex w-[200px] items-center gap-[14px]"
      :class="{
        'opacity-50': (isMaxKeywords || isDraggingKeywordInList) && draggingKeyword
      }"
    >
      <div
        class="block shrink-0 rounded-full"
        :style="{
          width: '8px',
          height: '8px',
          backgroundColor: color
        }"
      />
      <span
        v-tooltip="listName"
        class="v3-body-regular-400-line-height-1.5 select-none truncate
        text-[--v3-global-textsecondarycolor] transition-colors duration-150 group-hover:text-[--v3-global-textcolor]"
        :class="{
          '!text-[--v3-global-textcolor]': (isDragOver && !draggingKeyword) || selected,
        }"
      >
        {{ listName }}
      </span>
    </div>
    <div class="pointer-events-none relative flex h-[21px] w-1/2 items-center">
      <Transition>
        <span v-if="loading" class="absolute right-0 flex items-center">
          <ProgressSpinner class="size-[20px]" />
        </span>
        <span
          v-else-if="isMaxKeywords && !!draggingKeyword"
          class="v3-body-regular-400-line-height-1.5 absolute right-0 select-none !tracking-[-2px] text-[--v3-red-red-300]"
        >
          {{ keywords.length }} / {{ max }}
        </span>
        <span v-else-if="isDraggingKeywordInList" class="absolute right-0 flex items-center">
          <Icon class="size-[14px] text-[--v3-green-green-700]" name="check" />
        </span>
        <span
          v-else-if="!addingKeyword"
          class="v3-body-regular-400-line-height-1.5 absolute right-0 select-none text-[--v3-input-field-inputplaceholdertextcolor]"
        >
          {{ keywords.length }}
        </span>
      </Transition>
      <Transition name="adding">
        <span
          v-if="addingKeyword"
          class="v3-body-regular-400-line-height-1.5 absolute right-0 text-[--v3-green-green-700]"
        >
          +1
        </span>
      </Transition>
    </div>
  </div>
</template>

<script setup lang='ts'>
import ProgressSpinner from 'primevue/progressspinner';
import { computed, ref, watch } from 'vue';

import Icon from '@/common/components/atoms/Icon';
import { mixPanelEvents } from '@/common/constants';
import { track } from '@/helpers/analytics/mixpanel';

interface Props {
  color: string
  draggingKeyword?: string | null
  keywords: {
    id: number
    keyword: string
  }[]
  listId: number
  listName: string
  loading?: boolean
  max: number
  selected?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  selected: false,
  loading: false,
  draggingKeyword: null,
});

const emit = defineEmits<{
  onAddKeywordToList: [listId: number]
}>();

const addingKeyword = ref<boolean>(false);
const isDragOver = ref<boolean>(false);
const isMaxKeywords = computed<boolean>(() => props.keywords.length >= props.max);
const isDraggingKeywordInList = computed<boolean>(() => props.keywords.map((item) => item.keyword).includes(props.draggingKeyword));
const canAddKeywords = computed<boolean>(() => !isMaxKeywords.value && !isDraggingKeywordInList.value);

const handleDrop = () => {
  if (canAddKeywords.value) {
    emit('onAddKeywordToList', props.listId);
    track(mixPanelEvents.DRAGGED_KEYWORD_TO_LIST);
  }
};

const handleDragEnter = () => {
  isDragOver.value = true;
};

const handleDragLeave = () => {
  isDragOver.value = false;
};

watch(() => props.keywords.length, (newVal, oldVal) => {
  if (newVal > oldVal) {
    addingKeyword.value = true;
    setTimeout(() => {
      addingKeyword.value = false;
    }, 200);
  }
});

watch(() => props.draggingKeyword, (newVal, oldVal) => {
  if (!!oldVal && !newVal) {
    isDragOver.value = false;
  }
});
</script>

<style scoped>
.v-enter-from,
.v-leave-to,
.adding-leave-from,
.adding-leave-to
{
  @apply opacity-0;
}

.v-enter-active,
.v-leave-active,
.adding-enter-active
{
  @apply transition-opacity duration-150;
}

.v-enter-to,
.v-leave-from,
.adding-enter-to,
.adding-leave-from
{
  @apply opacity-100;
}

.adding-leave-active {
  @apply transition-[opactiy_transform] duration-1000;
}

.adding-leave-to {
  @apply translate-y-[-20px];
}
</style>
